// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './MultiSelectDropdown.scss';

export default function MultiSelectDropdown({ options, setSelectedOptions, defaultOptions, title, searchPlaceholder = 'search_suppliers', noResultsTranslationKey = 'no_results_found' }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState(defaultOptions || []);
  const dropdownRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter options based on search term
  const filteredOptions = options.filter(option => 
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle selection of an option
  const toggleOption = (option) => {
    const isSelected = selectedItems.some(item => item.id === option.id);
    let newSelection;
    
    if (isSelected) {
      newSelection = selectedItems.filter(item => item.id !== option.id);
    } else {
      newSelection = [...selectedItems, option];
    }
    
    setSelectedItems(newSelection);
    setSelectedOptions(newSelection);
  };

  // Clear all selections
  const clearSelections = () => {
    setSelectedItems([]);
    setSelectedOptions([]);
  };

  // Toggle "All" option
  const toggleAll = () => {
    if (selectedItems.length === 0 || selectedItems.length < options.length - 1) {
      // If none or some are selected, select all except the "All" option
      const allOptionsExceptAll = options.filter(option => option.id !== 0);
      setSelectedItems(allOptionsExceptAll);
      setSelectedOptions(allOptionsExceptAll);
    } else {
      // If all are selected, clear all
      clearSelections();
    }
  };

  // Get display text for the button
  const getButtonText = () => {
    if (selectedItems.length === 0) {
      return options[0].name; // "All" is typically the first option
    } else if (selectedItems.length === 1) {
      return selectedItems[0].name;
    } else {
      return `${selectedItems.length} ${t('multi_select_title') || 'selected'}`;
    }
  };

  return (
    <div className={`multi-select-dropdown ${isOpen ? 'dropdown-open' : ''}`} ref={dropdownRef}>
      <div 
        className="dropdown-splitbutton"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="dropdown-splitbutton-text">{title}:</span>
        <button className="dropdown-splitbutton-button dropdown-splitbutton-button-large">
          {getButtonText()}
        </button>
      </div>
      
      <div className={`dropdown-content ${isOpen ? 'dropdown-content-open' : ''}`}>
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder={t(searchPlaceholder) || "Search..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          {searchTerm && (
            <button 
              className="clear-search"
              onClick={(e) => {
                e.stopPropagation();
                setSearchTerm('');
              }}
            >
              ×
            </button>
          )}
        </div>

        <div className="options-container">
          <div className="option select-all" onClick={(e) => {
            e.stopPropagation();
            toggleAll();
          }}>
            <div className={`checkbox ${selectedItems.length === options.length - 1 ? 'checked' : ''}`}>
              {selectedItems.length === options.length - 1 && <span>✓</span>}
            </div>
            <span className="option-label">{options[0].name}</span>
          </div>
          
          {filteredOptions.length > 0 ? (
            filteredOptions.filter(option => option.id !== 0).map(option => (
              <div 
                key={option.id} 
                className="option"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleOption(option);
                }}
              >
                <div className={`checkbox ${selectedItems.some(item => item.id === option.id) ? 'checked' : ''}`}>
                  {selectedItems.some(item => item.id === option.id) && <span>✓</span>}
                </div>
                <span className="option-label">{option.name}</span>
              </div>
            ))
          ) : (
            <div className="no-results">
              {t(noResultsTranslationKey) || "No results found"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 